import React, { useState, useEffect, useMemo } from 'react';
import axiosInstance from '../api/axiosInstance';
import MultiSelectSupervisor from './MultiSelectSupervisor';
import DesignChangeModal from './DesignChangeModal';
import CompletionPaymentModal from './CompletionPaymentModal';
import FinalCompletionAmountModal from './FinalCompletionAmountModal'; // New import
import '../App.css';
import './TaskComponent.css';
import { useAuth } from '../context/AuthContext';
import { useAtomValue } from 'jotai';
import { branchesState } from '../jotai/atoms';
// import Swal from 'sweetalert2';

function TaskDetailModal({ isOpen, onClose, task, onUpdateTask, onDeleteTask, isEditMode, supervisors, contractors }) {
  const [editedTask, setEditedTask] = useState(task);
  const [selectedManagers, setSelectedManagers] = useState([]);
  const [selectedAssistants, setSelectedAssistants] = useState([]);
  const [scheduleHistory, setScheduleHistory] = useState([]);
  const [isDesignChangeModalOpen, setIsDesignChangeModalOpen] = useState(false);
  const [isCompletionPaymentModalOpen, setIsCompletionPaymentModalOpen] = useState(false);
  const [isFinalCompletionAmountModalOpen, setIsFinalCompletionAmountModalOpen] = useState(false); // New state
  const [currentPage, setCurrentPage] = useState(1);
  const { user } = useAuth();
  const branches = useAtomValue(branchesState); // Get branches from Jotai

  const [headquartersOptions, setHeadquartersOptions] = useState([]);
  const [branchMap, setBranchMap] = useState({});
  const [currentBranchOptions, setCurrentBranchOptions] = useState([]);

  useEffect(() => {
    if (branches && branches.length > 0) {
      const uniqueHQs = [...new Set(branches.map(branch => branch.headquarters))];
      setHeadquartersOptions(uniqueHQs);

      const newBranchMap = branches.reduce((acc, branch) => {
        if (!acc[branch.headquarters]) {
          acc[branch.headquarters] = [];
        }
        acc[branch.headquarters].push(branch.name);
        return acc;
      }, {});
      setBranchMap(newBranchMap);
    }
  }, [branches]);

  useEffect(() => {
    if (editedTask.headquarters && branchMap[editedTask.headquarters]) {
      setCurrentBranchOptions(branchMap[editedTask.headquarters]);
    } else {
      setCurrentBranchOptions([]);
    }
  }, [editedTask.headquarters, branchMap]);

  const handleAddressSearch = () => {
    const width = 500; //팝업의 너비
    const height = 600; //팝업의 높이
    new window.daum.Postcode({
      oncomplete: function(data) {
        let eupMyeonDongPart = '';
        if (data.bname1 && data.bname1.trim() !== '') {
            eupMyeonDongPart = data.bname1; // Use bname1 (e.g., "초월읍")
        } else if (data.bname && data.bname.trim() !== '') {
            eupMyeonDongPart = data.bname; // Fallback to bname (e.g., "송정동")
        } else if (data.eupmyeondong && data.eupmyeondong.trim() !== '') {
            eupMyeonDongPart = data.eupmyeondong; // Last resort
        }
        const formattedAddress = `${data.sido} ${data.sigungu} ${eupMyeonDongPart}`;

        setEditedTask(prev => ({ ...prev, address: formattedAddress }));
      }
    }).open({
      left: (window.screen.width / 2) - (width / 2),
      top: (window.screen.height / 2) - (height / 2)
    });
  };

  useEffect(() => {
    // When the task (and its schedule history) changes, reset to the first page
    setCurrentPage(1);
  }, [task]);

  useEffect(() => {
    setEditedTask(prev => {
      const isSameTask = prev?.id === task?.id;
      return {
        ...task,
        remarks: task?.remarks || '',
        designChangeHistory: task?.designChangeHistory || [],
        completionPaymentHistory: task?.completionPaymentHistory || [],
        completionAmount: task.completionAmount ?? (isSameTask ? prev?.completionAmount : undefined),
        changeAmount: task.changeAmount ?? (isSameTask ? prev?.changeAmount : undefined),
        completionReportStatus: task?.completionReportStatus || '미완료',
      }
    });

    setSelectedManagers(
      typeof task.managers === 'string' ? task.managers.split(',') : (task.managers || [])
    );
    setSelectedAssistants(
      typeof task.assistants === 'string' ? task.assistants.split(',') : (task.assistants || [])
    );

    if (task && task.id) {
      fetchScheduleHistory(task.id);
    }
  }, [task]);

  useEffect(() => {
    if (isEditMode) {
        setEditedTask(prev => ({
            ...prev,
            managers: selectedManagers,
            assistants: selectedAssistants,
        }));
    }
}, [selectedManagers, selectedAssistants, isEditMode]);


  const fetchScheduleHistory = async (taskId) => {
    try {
      const response = await axiosInstance.get(`/schedule-entries/task/${taskId}`);
      setScheduleHistory(response.data);
    } catch (error) {
      console.error('Error fetching schedule history:', error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    let formattedValue = value;

    if (name === 'headquarters') {
      setEditedTask(prev => ({ ...prev, headquarters: value, branch: '' }));
      return;
    }

    if (name === 'number' || name === 'mainTaskNumber') {
      // 대문자 알파벳과 숫자만 남기고 모든 문자 제거
      formattedValue = value.toUpperCase().replace(/[^A-Z0-9]/g, '');
      // 4자리-4자리-5자리 형식으로 하이픈 추가 (이 부분은 필요에 따라 조정할 수 있습니다)
      if (formattedValue.length > 4 && formattedValue.length <= 8) {
        formattedValue = formattedValue.replace(/([A-Z0-9]{4})([A-Z0-9]+)/, '$1-$2');
      } else if (formattedValue.length > 8) {
        formattedValue = formattedValue.replace(/([A-Z0-9]{4})([A-Z0-9]{4})([A-Z0-9]+)/, '$1-$2-$3');
      }
    }

    setEditedTask(prev => ({ ...prev, [name]: formattedValue }));
  };

  const handleDesignChangeSave = ({ amount, date }) => {
    const newTotalAmount = Number(amount);
    const initialAmount = Number(editedTask.initialContractAmount || 0);

    const newChangeAmount = newTotalAmount - initialAmount;

    const newHistoryEntry = { amount: newTotalAmount, date };
    const newHistory = [...(editedTask.designChangeHistory || []), newHistoryEntry];

    setEditedTask(prev => ({
        ...prev,
        designChangeHistory: newHistory,
        changeAmount: newChangeAmount,
    }));

    setIsDesignChangeModalOpen(false);
    onUpdateTask({
      ...editedTask,
      designChangeHistory: newHistory,
      changeAmount: newChangeAmount,
    });
  };

  const handleCompletionPaymentSave = ({ amount, date }) => {
    const newHistoryEntry = { amount, date };
    const newHistory = [...(editedTask.completionPaymentHistory || []), newHistoryEntry];
    const totalCompletionAmount = newHistory.reduce((acc, item) => acc + Number(item.amount), 0);

    // Calculate the new balance after this completion payment
    const currentContractAmount = Number(editedTask.initialContractAmount || 0) + Number(editedTask.changeAmount || 0);
    const newBalance = currentContractAmount - totalCompletionAmount;

    // Determine the type for the history entry
    const historyType = (newBalance === 0) ? '기성(준공)' : '기성';
    newHistoryEntry.type = historyType; // Add type to the history entry

    setEditedTask(prev => ({
      ...prev,
      completionPaymentHistory: newHistory,
      completionAmount: totalCompletionAmount,
    }));

    setIsCompletionPaymentModalOpen(false);
    onUpdateTask({
      ...editedTask,
      completionPaymentHistory: newHistory,
      completionAmount: totalCompletionAmount,
    });
  };

  const handleFinalCompletionAmountSave = ({ amount, date }) => {
    const newHistoryEntry = { amount: Number(amount), date, type: '준공', round: 0 }; // Store entered amount, type '준공', round '0차'
    const newHistory = [...(editedTask.completionPaymentHistory || []), newHistoryEntry]; // Add to completionPaymentHistory

    const newCompletionAmount = Number(amount); // Overwrite completionAmount

    setEditedTask(prev => ({
      ...prev,
      completionPaymentHistory: newHistory,
      completionAmount: newCompletionAmount,
    }));

    setIsFinalCompletionAmountModalOpen(false);
    onUpdateTask({
      ...editedTask,
      completionPaymentHistory: newHistory,
      completionAmount: newCompletionAmount,
    });
  };

  const handleDeleteHistoryEntry = (itemToDelete) => {
    let updatedTask = { ...editedTask };

    if (itemToDelete.type === '설계변경') {
      const newDesignChangeHistory = editedTask.designChangeHistory.filter(
        (item) => item.date !== itemToDelete.date || item.amount !== itemToDelete.amount
      );
      const initialAmount = Number(editedTask.initialContractAmount || 0);
      let newChangeAmount = 0;
      if (newDesignChangeHistory.length > 0) {
        const latestDesignChangeAmount = Number(newDesignChangeHistory[newDesignChangeHistory.length - 1].amount);
        newChangeAmount = latestDesignChangeAmount - initialAmount;
      }
      updatedTask = { ...updatedTask, designChangeHistory: newDesignChangeHistory, changeAmount: newChangeAmount };
    } else if (itemToDelete.type === '기성' || itemToDelete.type === '준공' || itemToDelete.type === '기성(준공)') {
      const newCompletionPaymentHistory = editedTask.completionPaymentHistory.filter(
        (item) => item.date !== itemToDelete.date || item.amount !== itemToDelete.amount
      );
      const newCompletionAmount = newCompletionPaymentHistory.reduce((acc, item) => acc + Number(item.amount), 0);
      updatedTask = { ...updatedTask, completionPaymentHistory: newCompletionPaymentHistory, completionAmount: newCompletionAmount };
    }

    setEditedTask(updatedTask);
    onUpdateTask(updatedTask);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const taskToSubmit = {
      ...editedTask,
      managers: selectedManagers,
      assistants: selectedAssistants,
    };
    onUpdateTask(taskToSubmit);
  };

  const handleDelete = () => {
    onDeleteTask(task.id);
  };

  const assistantCounts = useMemo(() => {
    const counts = {};
    scheduleHistory.forEach(entry => {
      if (entry.assignedAssistants) {
        const assistants = entry.assignedAssistants.split(',').map(name => name.trim());
        assistants.forEach(assistant => {
          if (assistant) { // Ensure not to count empty strings
            counts[assistant] = (counts[assistant] || 0) + 1;
          }
        });
      }
    });
    return counts;
  }, [scheduleHistory]);

  if (!isOpen) return null;

  const designChangeHistoryWithRound = (editedTask.designChangeHistory || []).map((item, index) => ({
    ...item,
    type: '설계변경',
    round: index + 1,
  }));

  const completionPaymentHistoryWithRound = (editedTask.completionPaymentHistory || []).map((item, index) => ({
    ...item,
    type: item.type || '기성',
    round: item.type === '준공' ? 0 : index + 1, // If type is '준공', set round to 0, else index + 1
  }));

  const combinedHistory = [...designChangeHistoryWithRound, ...completionPaymentHistoryWithRound]
    .sort((a, b) => new Date(a.date) - new Date(b.date));

  const statusConfig = {
    '배치완료': { color: '#4CAF50', order: 1, label: '배치완료' },
    '배치지시': { color: '#2196f3', order: 2, label: '배치지시' },
    '배치계획': { color: '#ffc107', order: 3, label: '배치계획' },
    'unknown': { color: '#e0e0e0', order: 4, label: '미정' }
  };

  let progressBarSegments = [];
  let totalPercentage = 0;

  if (editedTask.taskStatus === '준공') {
    progressBarSegments.push({
      percentage: 100,
      color: statusConfig['배치완료'].color,
      status: '준공',
      count: scheduleHistory.length
    });
    totalPercentage = 100;
  } else if (scheduleHistory.length > 0 && editedTask.period > 0) {
    const statusCounts = scheduleHistory.reduce((acc, entry) => {
      const status = entry.deploymentStatus || 'unknown';
      acc[status] = (acc[status] || 0) + 1;
      return acc;
    }, {});

    progressBarSegments = Object.keys(statusCounts).map(status => {
      const count = statusCounts[status];
      const percentage = (count / editedTask.period) * 100;
      totalPercentage += percentage;
      return {
        percentage,
        color: statusConfig[status]?.color || statusConfig['unknown'].color,
        status: statusConfig[status]?.label || statusConfig['unknown'].label,
        count: count,
        order: statusConfig[status]?.order || statusConfig['unknown'].order
      };
    }).sort((a, b) => a.order - b.order);
    totalPercentage = Math.min(totalPercentage, 100);
  }

  const itemsPerPage = 20;
  const sortedHistoryForPagination = [...scheduleHistory].sort((a, b) => new Date(b.scheduledDate) - new Date(a.scheduledDate));
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = sortedHistoryForPagination.slice(indexOfFirstItem, indexOfLastItem);

  const pageNumbers = [];
  for (let i = 1; i <= Math.ceil(sortedHistoryForPagination.length / itemsPerPage); i++) {
    pageNumbers.push(i);
  }

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  return (
    <>
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
          <button onClick={onClose} className="modal-close-button">×</button>
          <h2>공사 {isEditMode ? '수정' : '상세 정보'}</h2>
          <div>
              <p>
                작업 연도 : {
                  new Date(task.contractDate).getFullYear() === Number(editedTask.workYear)
                    ? `${editedTask.workYear}년`
                    : `${new Date(task.contractDate).getFullYear()} ~ ${editedTask.workYear}년`
                }
              </p> 
          </div>
          <br/><hr/><br/>
          <form onSubmit={handleSubmit} onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
            }
          }}>
            <div className="form-grid">
                <div className="form-group">
                    <label>1. 번호</label>
                    <input className="form-control" type="text" name="displayNumber" value={editedTask.displayNumber || ''} onChange={handleInputChange} readOnly={!isEditMode} />
                </div>
                <div className="form-group">
                    <label>2. 공사명</label>
                    <input className="form-control" type="text" name="name" value={editedTask.name || ''} onChange={handleInputChange} readOnly={!isEditMode} />
                </div>
                <div className="form-group">
                    <label>3. 공사번호(감리)</label>
                    <input className="form-control" type="text" name="number" value={editedTask.number || ''} onChange={handleInputChange} readOnly={!isEditMode} />
                </div>
                <div className="form-group">
                    <label>4. 공사번호(본공사)</label>
                    <input className="form-control" type="text" name="mainTaskNumber" value={editedTask.mainTaskNumber || ''} onChange={handleInputChange} readOnly={!isEditMode} />
                </div>
                <div className="form-group">
                    <label>5. 본부</label>
                    {isEditMode ? (
                        <select className="form-control" name="headquarters" value={editedTask.headquarters || ''} onChange={handleInputChange}>
                            <option value="">선택하세요</option>
                            {headquartersOptions.map(hq => <option key={hq} value={hq}>{hq}</option>)}
                        </select>
                    ) : (
                        <input className="form-control" type="text" value={editedTask.headquarters || ''} readOnly />
                    )}
                </div>
                <div className="form-group">
                    <label>6. 지사</label>
                    {isEditMode ? (
                        <select className="form-control" name="branch" value={editedTask.branch || ''} onChange={handleInputChange}>
                            <option value="">선택하세요</option>
                            {currentBranchOptions.map(b => <option key={b} value={b}>{b}</option>)}
                        </select>
                    ) : (
                        <input className="form-control" type="text" value={editedTask.branch || ''} readOnly />
                    )}
                </div>
                <div className="form-group">
                    <label>7. 종류</label>
                    <select className="form-control" name="type" value={editedTask.type || ''} onChange={handleInputChange} disabled={!isEditMode}>
                        <option value="">선택하세요</option>
                        <option value="단가">단가</option>
                        <option value="총가">총가</option>
                    </select>
                </div>
                <div className="form-group">
                    <label>8. 공기일</label>
                    <input className="form-control" type="text" name="period" value={editedTask.period || ''} onChange={handleInputChange} readOnly={!isEditMode} />
                </div>

                <div className="form-group">
                    <label>9. 책임감리</label>
                    <MultiSelectSupervisor supervisors={supervisors} selectedSupervisors={selectedManagers} onSelectionChange={setSelectedManagers} placeholder="책임감리원을 선택하세요" disabled={!isEditMode} isManager={true} otherSelectedSupervisors={selectedAssistants} />
                </div>
                <div className="form-group">
                    <label>10. 보조감리</label>
                    <MultiSelectSupervisor supervisors={supervisors} selectedSupervisors={selectedAssistants} onSelectionChange={setSelectedAssistants} placeholder="보조감리원을 선택하세요" disabled={!isEditMode} isManager={false} otherSelectedSupervisors={selectedManagers} />
                </div>
                <div className="form-group">
                    <label>11. 한전담당</label>
                    <input className="form-control" type="text" name="kepcoManager" value={editedTask.kepcoManager || ''} onChange={handleInputChange} readOnly={!isEditMode} />
                </div>
                <div className="form-group">
                    <label>12. 시공사</label>
                    <select className="form-control" name="company" value={editedTask.company || ''} onChange={handleInputChange} disabled={!isEditMode}>
                        <option value="">선택하세요</option>
                        {contractors && contractors.map((contractor) => (
                            <option key={contractor.id} value={contractor.name}>
                                {contractor.name}
                            </option>
                        ))}
                    </select>
                </div>
                <div className="form-group">
                    <label>13. 공사 계약일</label>
                    <input className="form-control" type="date" name="contractDate" value={editedTask.contractDate || ''} onChange={handleInputChange} readOnly={!isEditMode} />
                </div>
                <div className="form-group">
                    <label>14. 공사 기간</label>
                    <input className="form-control" type="text" name="constructionPeriod" value={editedTask.constructionPeriod || ''} onChange={handleInputChange} readOnly={!isEditMode} placeholder="YYYY-MM-DD ~ YYYY-MM-DD" />
                </div>
                <div className="form-group">
                    <label>15. 현장주소(읍/면/동)</label>
                    {isEditMode ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                            <input
                                className="form-control"
                                type="text"
                                name="address"
                                value={editedTask.address || ''}
                                readOnly // 주소는 검색을 통해서만 입력되므로 읽기 전용
                                placeholder="주소 검색 버튼을 클릭하세요"
                                style={{ flexGrow: 1 }}
                            />
                            <button type="button" className="action-button" onClick={handleAddressSearch} style={{ whiteSpace: 'nowrap' }}>
                                주소 검색
                            </button>
                        </div>
                    ) : (
                        <input className="form-control" type="text" value={editedTask.address || ''} readOnly />
                    )}
                </div>
                <div className="form-group">
                    <label>16. 진행현황</label>
                    {isEditMode ? (
                        <select className="form-control" name="taskStatus" value={editedTask.taskStatus || '공사중'} onChange={handleInputChange}>
                            <option value="공사중">공사중</option>
                            <option value="준공">준공</option>
                        </select>
                    ) : (
                        <input className="form-control" type="text" value={editedTask.taskStatus || '공사중'} readOnly />
                    )}
                </div>
                <div className="form-group">
                    <label>17. 완료보고현황</label>
                    {isEditMode ? (
                        <select className="form-control" name="completionReportStatus" value={editedTask.completionReportStatus || '미완료'} onChange={handleInputChange}>
                            <option value="미완료">미완료</option>
                            <option value="완료">완료</option>
                        </select>
                    ) : (
                        <input className="form-control" type="text" value={editedTask.completionReportStatus || '미완료'} readOnly />
                    )}
                </div>
                <div className="form-group">
                    <label>18. 공사유형</label>
                    {isEditMode ? (
                        <select className="form-control" name="constructionType" value={editedTask.constructionType || '미정'} onChange={handleInputChange}>
                            <option value="미정">미정</option>
                            <option value="신규">신규</option>
                            <option value="보수">보수</option>
                            <option value="운영">운영</option>
                        </select>
                    ) : (
                        <input className="form-control" type="text" value={editedTask.constructionType || '미정'} readOnly />
                    )}
                </div>
                <div className="form-group">
                    <label>19. 최초계약금액</label>
                    <input className="form-control" type="text" name="initialContractAmount" value={editedTask.initialContractAmount || ''} onChange={handleInputChange} readOnly={!isEditMode} placeholder="최초계약금액을 입력하세요" />
                </div>
                <div className="form-group">
                    <label>20. 증감금액(설계변경)</label>
                    <input className="form-control" type="text" name="changeAmount" value={(editedTask.changeAmount || 0).toLocaleString()} readOnly />
                </div>
                <div className="form-group">
                    <label>21. 계약금액</label>
                    <input className="form-control" type="text" name="contractAmount" value={(Number(editedTask.initialContractAmount || 0) + Number(editedTask.changeAmount || 0)).toLocaleString()} readOnly />
                </div>
                <div className="form-group">
                    <label>22. 준공금액(기성)</label>
                    <input className="form-control" type="text" name="completionAmount" value={(Number(editedTask.completionAmount || 0)).toLocaleString()} readOnly />
                </div>
                <div className="form-group">
                    <label>23. 잔액</label>
                    <input className="form-control" type="text" name="balance" value={(Number(editedTask.initialContractAmount || 0) + Number(editedTask.changeAmount || 0) - Number(editedTask.completionAmount || 0)).toLocaleString()} readOnly />
                </div>
            </div>

            {isEditMode && (
              <div className="form-actions-top">
                <button type="button" className="action-button" onClick={() => setIsDesignChangeModalOpen(true)}>설계변경</button>
                <button type="button" className="action-button" onClick={() => setIsCompletionPaymentModalOpen(true)}>기성금액</button>
                <button
                  type="button"
                  className="action-button"
                  onClick={() => setIsFinalCompletionAmountModalOpen(true)}
                  disabled={(editedTask.completionPaymentHistory || []).length > 0} // Disable if history exists
                >
                  준공금액
                </button>
              </div>
            )}

            {combinedHistory.length > 0 && (
              <div className="history-table-section">
                <h4>변경 이력</h4>
                <table className="history-table">
                  <thead>
                    <tr>
                      <th>구분</th>
                      <th>차수</th>
                      <th>금액</th>
                      <th>날짜</th>
                      <th>관리</th>
                    </tr>
                  </thead>
                  <tbody>
                    {combinedHistory.map((item, index) => (
                      <tr key={index}>
                        <td>{item.type}</td>
                        <td>{item.round}차</td>
                        <td>{Number(item.amount).toLocaleString()}</td>
                        <td>{item.date}</td>
                        <td><button onClick={() => handleDeleteHistoryEntry(item)}>삭제</button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div className="remarks-section">
              <label>공사개요</label>
              <textarea name="constructionOutline" value={editedTask.constructionOutline || ''} onChange={handleInputChange} readOnly={!isEditMode}></textarea>
            </div>
            <div className="remarks-section">
              <label>비고</label>
              <textarea name="remarks" value={editedTask.remarks || ''} onChange={handleInputChange} readOnly={!isEditMode}></textarea>
            </div>

            {/* {isEditMode && (
              <div className="form-actions">
                <button type="submit" className="submit-button">수정 완료</button>
                <button type="button" onClick={handleDelete} className="delete-button" style={{ marginLeft: '10px' }}>삭제하기</button>
              </div>
            )} */}
          {user && user.role === 'ADMIN' && isEditMode && (
            <div className="taskDetail-actions-container"> {/* New container for buttons */}
              <button type="submit" className="taskDetail-edit-button">수정</button>
              <button type="button" onClick={handleDelete} className="taskDetail-delete-button">삭제</button>
            </div>
          )}
          </form>

          <div className="history-table-section">
            <h3>일정 기록 {'\u00A0'}{'\u00A0'}{(() => {
              const formatDate = (dateStr) => dateStr.substring(2).replace(/-/g, '.');
              if (scheduleHistory.length === 0) {
                return `(${scheduleHistory.length})`;
              }
              const sortedHistory = [...scheduleHistory].sort((a, b) => new Date(a.scheduledDate) - new Date(b.scheduledDate));
              const firstDate = sortedHistory[0].scheduledDate;
              const lastDate = sortedHistory[sortedHistory.length - 1].scheduledDate;
              if (firstDate === lastDate) {
                return `${formatDate(firstDate)} (${scheduleHistory.length}일)`;
              }
              return `${formatDate(firstDate)} ~ ${formatDate(lastDate)} (${scheduleHistory.length}일)`;
            })()}</h3>
            {Object.keys(assistantCounts).length > 0 && (
              <div style={{ marginTop: '10px', marginBottom: '10px', fontSize: '0.9em' }}>
                <strong>보조감리 투입 현황:</strong>
                {Object.entries(assistantCounts).map(([name, count]) => (
                  <span key={name} style={{ marginLeft: '10px' }}>{name}: {count}회</span>
                ))}
                <span style={{ marginLeft: '20px', fontWeight: 'bold' }}>총 {Object.values(assistantCounts).reduce((sum, count) => sum + count, 0)}회</span>
              </div>
            )}
            {editedTask.period && (
              <div style={{ marginTop: '10px', marginBottom: '10px' }}>
                <div style={{
                  height: '24px',
                  backgroundColor: '#f0f0f0',
                  borderRadius: '12px',
                  overflow: 'hidden',
                  position: 'relative',
                  border: '1px solid #ccc',
                  display: 'flex'
                }}>
                  {progressBarSegments.map((segment, index) => (
                    <div key={index} title={`${segment.status}: ${segment.count}일`} style={{
                      width: `${segment.percentage}%`,
                      height: '100%',
                      backgroundColor: segment.color,
                      transition: 'width .5s ease-in'
                    }}></div>
                  ))}
                  <div style={{
                    position: 'absolute',
                    top: '0',
                    left: '0',
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: totalPercentage > 50 ? 'white' : 'black',
                    fontWeight: 'bold',
                    textShadow: '0 0 2px rgba(0,0,0,0.5)'
                  }}>
                    {editedTask.taskStatus === '준공'
                      ? '100% (준공)'
                      : `${totalPercentage.toFixed(2)}% (${scheduleHistory.length}일 / ${editedTask.period}일)`
                    }
                  </div>
                </div>
                {progressBarSegments.length > 1 && editedTask.taskStatus !== '준공' && (
                  <div style={{ display: 'flex', justifyContent: 'center', marginTop: '5px', flexWrap: 'wrap', gap: '10px' }}>
                    {progressBarSegments.map((segment, index) => (
                      <div key={index} style={{ display: 'flex', alignItems: 'center' }}>
                        <div style={{ width: '12px', height: '12px', backgroundColor: segment.color, marginRight: '5px', border: '1px solid #ccc' }}></div>
                        <span>{`${segment.status}: ${segment.count}일`}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
            {scheduleHistory.length > 0 ? (
              <>
                <table className="main-table">
                  <thead>
                    <tr>
                      <th>날짜</th>
                      <th>책임감리</th>
                      <th>보조감리</th>
                      <th>배치상태</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentItems.map(entry => (
                      <tr key={entry.id}>
                        <td>
                          {entry.scheduledDate}
                          {( () => {
                            const today = new Date();
                            const entryDate = new Date(entry.scheduledDate);
                            today.setHours(0, 0, 0, 0);
                            entryDate.setHours(0, 0, 0, 0);
                            return today.getTime() === entryDate.getTime() ? <span style={{ marginLeft: '5px', fontWeight: 'bold', color: 'blue' }}>(Today)</span> : null;
                          })()}
                        </td>
                        <td>{entry.assignedManagers}</td>
                        <td>{entry.assignedAssistants || '없음'}</td>
                        <td style={{
                          backgroundColor: statusConfig[entry.deploymentStatus]?.color || statusConfig['unknown'].color,
                          color: 'white',
                          fontWeight: 'bold',
                          textAlign: 'center',
                          borderRadius: '4px',
                          padding: '2px 6px'
                        }}>
                          {statusConfig[entry.deploymentStatus]?.label || statusConfig['unknown'].label}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {pageNumbers.length > 1 && (
                  <div className="pagination">
                    {pageNumbers.map(number => (
                      <button key={number} onClick={() => paginate(number)} className={currentPage === number ? 'active' : ''}>
                        {number}
                      </button>
                    ))}
                  </div>
                )}
              </>
            ) : (
              <p>등록된 일정 기록이 없습니다.</p>
            )}
          </div>
        </div>
      </div>
      <DesignChangeModal isOpen={isDesignChangeModalOpen} onClose={() => setIsDesignChangeModalOpen(false)} onSave={handleDesignChangeSave} />
      <CompletionPaymentModal isOpen={isCompletionPaymentModalOpen} onClose={() => setIsCompletionPaymentModalOpen(false)} onSave={handleCompletionPaymentSave} />
      <FinalCompletionAmountModal
        isOpen={isFinalCompletionAmountModalOpen}
        onClose={() => setIsFinalCompletionAmountModalOpen(false)}
        onSave={handleFinalCompletionAmountSave}
        contractAmount={(Number(editedTask.initialContractAmount || 0) + Number(editedTask.changeAmount || 0))} // Pass calculated contractAmount
      />
    </>
  );
}

export default TaskDetailModal;